/**
 * Deputy compound alias registry.
 *
 * Keeps sensitive compound display labels local to the browser while allowing
 * UI surfaces to show local display names instead of bare masked ids. The
 * Orchestrator payload and canonical audit representation remain masked-only.
 *
 * @module deputy/compoundAliasRegistry
 */

import { indexToken } from '../gibbs/autonomousCampaign/workspaces/token';
import { defaultCompoundAliasStore } from './compoundAliasStore';

const MASKED_COMPOUND_RE = /\bCOMPOUND_[A-Z]+\b/g;
// Masked structure identities. Decoded for display exactly as compounds are: a
// narrative that names `PROTEIN_A` as the docking site while naming every
// compound around it in plain language does not actually tell the reader which
// protein the reported energies belong to, which was the point of saying it.
const MASKED_PROTEIN_RE = /\b(?:TARGET_X|PROTEIN_[A-Z]+)\b/g;
const STORE_VERSION = 2;
const MAX_LABEL_LENGTH = 80;
const MARKDOWN_ESCAPE_CHARS = new Set(Array.from('\\`*_{}[]()#+-.!|'));

function maskedCompoundIdForIndex(index) {
    return Number.isInteger(index) && index >= 0 ? `COMPOUND_${indexToken(index)}` : null;
}

function shortHash(value) {
    const text = String(value || '');
    let hash = 2166136261;
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
}

function molTopologyIdentity(mol2000) {
    if (typeof mol2000 !== 'string') return null;
    const lines = mol2000.replace(/\r\n?/g, '\n').split('\n');
    const countsIndex = lines.findIndex((line, index) => (
        index === 3 && /^\s*\d+\s+\d+\s+.*\bV2000\s*$/.test(line)
    ));
    if (countsIndex < 0) return null;
    const counts = lines[countsIndex].trim().split(/\s+/);
    const atomCount = Number.parseInt(counts[0], 10);
    const bondCount = Number.parseInt(counts[1], 10);
    if (!Number.isInteger(atomCount) || !Number.isInteger(bondCount)
        || atomCount < 0 || bondCount < 0
        || lines.length < countsIndex + 1 + atomCount + bondCount) return null;

    const atomTopology = lines
        .slice(countsIndex + 1, countsIndex + 1 + atomCount)
        .map((line) => line.slice(30).trim().replace(/\s+/g, ' '));
    const bondTopology = lines
        .slice(countsIndex + 1 + atomCount, countsIndex + 1 + atomCount + bondCount)
        .map((line) => line.trim().replace(/\s+/g, ' '));
    const properties = lines
        .slice(countsIndex + 1 + atomCount + bondCount)
        .filter((line) => /^M\s{2}/.test(line) && !/^M\s{2}END/.test(line))
        .map((line) => line.trim().replace(/\s+/g, ' '));
    return [
        `${atomCount}:${bondCount}`,
        ...atomTopology,
        ...bondTopology,
        ...properties,
    ].join('|');
}

/**
 * Opaque browser-local compound identity used only to rebind a prior masked ID
 * after reload. Raw structure/name material is never persisted or sent.
 */
export function compoundLocalIdentityKey(compound) {
    let structure = null;
    let placedStructure = null;
    try {
        structure = compound?.getProperty?.('structure2d', 'inchi')
            || compound?.getUnnamedSmiles?.()
            || compound?.getSmiles?.()
            || compound?.smiles
            || null;
        placedStructure = compound?.getMol2000?.() || compound?.mol2000 || null;
    } catch (_) {
        return null;
    }
    const stableStructure = typeof structure === 'string' && structure.trim()
        ? structure.trim()
        : null;
    const topology = stableStructure || molTopologyIdentity(placedStructure);
    if (!topology) return null;
    // `resSpec` is the browser-local key Workspace.addReplaceCompounds uses to
    // replace a minimized pose. Include only an opaque digest so several poses
    // of the same molecular topology remain distinct without persisting or
    // sending their BMaps identifiers.
    const replacementKey = typeof compound?.resSpec === 'string' && compound.resSpec.trim()
        ? compound.resSpec.trim()
        : null;
    return [
        'CID',
        topology.length,
        shortHash(`structure:${topology}`),
        shortHash(`topology:${topology.length}:${topology}`),
        ...(replacementKey ? [
            'RID',
            replacementKey.length,
            shortHash(`replacement:${replacementKey}`),
        ] : []),
    ].join('_');
}

function sanitizeLabel(value) {
    if (value == null) return null;
    const label = String(value).replace(/\s+/g, ' ').trim();
    if (!label) return null;
    return label.slice(0, MAX_LABEL_LENGTH);
}

function escapeMarkdownInline(value) {
    const htmlSafe = String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    return Array.from(htmlSafe, (character) => (
        MARKDOWN_ESCAPE_CHARS.has(character) ? `\\${character}` : character
    )).join('');
}

function compoundDisplayName(compound) {
    return sanitizeLabel(compound?.displayName)
        || sanitizeLabel(compound?.getName?.())
        || sanitizeLabel(compound?.name);
}

function lineageFlags(maskedEntry = {}) {
    const out = {};
    if (maskedEntry.isNativeBinder === true) out.isNativeBinder = true;
    if (maskedEntry.isDockedPose === true) out.isDockedPose = true;
    return out;
}

export function workspaceFingerprint(workspace, maskedSnapshot = {}) {
    const proteins = Array.isArray(maskedSnapshot.proteins) ? maskedSnapshot.proteins : [];
    const compounds = Array.isArray(workspace?.getLoadedCompounds?.())
        ? workspace.getLoadedCompounds()
        : [];
    const proteinKey = proteins
        .map((protein) => [
            protein?.pdbId || '',
            protein?.isTarget === true ? 'target' : 'other',
        ].join(':'))
        .join('|');
    const labelKey = compounds
        .map((compound, index) => [
            index,
            compoundDisplayName(compound) || '',
            compoundLocalIdentityKey(compound) || '',
        ].join(':'))
        .join('|');
    const raw = [
        `proteins=${proteinKey}`,
        `compoundCount=${compounds.length}`,
        `fragInfoCount=${maskedSnapshot.fragInfoCount || 0}`,
        `labels=${labelKey}`,
    ].join(';');
    return `alias:${shortHash(raw)}`;
}

export function workspaceAliasScope(maskedSnapshot = {}) {
    const binding = maskedSnapshot.meta?.workspaceBindingId
        || maskedSnapshot.workspaceBindingId;
    if (typeof binding === 'string' && binding) return `binding:${binding}`;
    const proteins = Array.isArray(maskedSnapshot.proteins) ? maskedSnapshot.proteins : [];
    const proteinKey = proteins.map((protein) => [
        protein?.id || protein?.pdbId || '',
        protein?.isTarget === true ? 'target' : 'protein',
    ].join(':')).join('|');
    return proteinKey ? `proteins:${shortHash(proteinKey)}` : null;
}

export function buildAliasMap(workspace, maskedSnapshot = {}) {
    const compounds = Array.isArray(workspace?.getLoadedCompounds?.())
        ? workspace.getLoadedCompounds()
        : [];
    const maskedCompounds = Array.isArray(maskedSnapshot.compounds) ? maskedSnapshot.compounds : [];
    const aliases = {};

    compounds.forEach((compound, index) => {
        const snapshotId = maskedCompounds[index]?.id;
        const maskedId = typeof snapshotId === 'string' && /^COMPOUND_[A-Z]+$/.test(snapshotId)
            ? snapshotId
            : maskedCompoundIdForIndex(index);
        if (!maskedId) return;
        const displayName = compoundDisplayName(compound) || maskedId;
        const localIdentityKey = compoundLocalIdentityKey(compound);
        aliases[maskedId] = {
            maskedId,
            displayName,
            ...(localIdentityKey ? { localIdentityKey } : {}),
            ...lineageFlags(maskedCompounds[index]),
        };
    });

    return aliases;
}

/**
 * Masked structure id -> loaded structure name.
 *
 * Paired positionally, which is the same correspondence the masked snapshot is
 * built on: serializeWorkspace emits one protein per CaseData that has a
 * mapCase, and stateReader masks that list in order.
 *
 * Deliberately NOT merged into the persisted compound alias map. A compound mask
 * has to stay stable across reloads — that is what the store is for — whereas a
 * structure mask is positional and re-derived on every refresh, so persisting it
 * would add a second, staler source for something already free to recompute.
 *
 * @param {Object} workspace
 * @param {Object} maskedSnapshot
 * @returns {Object<string, {maskedId: string, displayName: string}>}
 */
export function buildProteinAliasMap(workspace, maskedSnapshot = {}) {
    const caseDatas = Array.isArray(workspace?.allCaseData?.())
        ? workspace.allCaseData()
        : [];
    const loaded = caseDatas.filter((caseData) => caseData?.mapCase);
    const maskedProteins = Array.isArray(maskedSnapshot.proteins) ? maskedSnapshot.proteins : [];
    const aliases = {};
    maskedProteins.forEach((protein, index) => {
        const maskedId = protein?.id;
        if (typeof maskedId !== 'string' || !/^(?:TARGET_X|PROTEIN_[A-Z]+)$/.test(maskedId)) return;
        const mapCase = loaded[index]?.mapCase;
        const displayName = sanitizeLabel(mapCase?.displayName || mapCase?.name);
        if (!displayName) return;
        aliases[maskedId] = { maskedId, displayName };
    });
    return aliases;
}

function aliasSignature(aliases) {
    return JSON.stringify(Object.keys(aliases || {}).sort().map((maskedId) => {
        const entry = aliases[maskedId] || {};
        return [
            maskedId,
            entry.displayName || '',
            entry.localIdentityKey || '',
            entry.isNativeBinder === true ? 'native' : '',
            entry.isDockedPose === true ? 'docked' : '',
        ];
    }));
}

export function formatMaskedCompoundId(maskedId, aliasMap, { includeMaskedId = true } = {}) {
    if (typeof maskedId !== 'string') return '';
    const entry = aliasMap?.aliases?.[maskedId] || aliasMap?.[maskedId];
    const displayName = sanitizeLabel(entry?.displayName);
    if (!displayName || displayName === maskedId) return maskedId;
    return includeMaskedId ? `${maskedId} · ${displayName}` : displayName;
}

export function formatMaskedProteinId(maskedId, aliasMap, { includeMaskedId = true } = {}) {
    if (typeof maskedId !== 'string') return '';
    const entry = aliasMap?.proteinAliases?.[maskedId];
    const displayName = sanitizeLabel(entry?.displayName);
    if (!displayName || displayName === maskedId) return maskedId;
    return includeMaskedId ? `${maskedId} · ${displayName}` : displayName;
}

export function formatMaskedText(text, aliasMap, options = {}) {
    if (typeof text !== 'string' || !text) return text;
    return text
        .replace(MASKED_COMPOUND_RE, (maskedId) => (
            formatMaskedCompoundId(maskedId, aliasMap, options)
        ))
        .replace(MASKED_PROTEIN_RE, (maskedId) => (
            formatMaskedProteinId(maskedId, aliasMap, options)
        ));
}

export function formatMaskedMarkdown(text, aliasMap, options = {}) {
    if (typeof text !== 'string' || !text) return text;
    return text
        .replace(MASKED_COMPOUND_RE, (maskedId) => {
            const formatted = formatMaskedCompoundId(maskedId, aliasMap, options);
            return formatted === maskedId ? maskedId : escapeMarkdownInline(formatted);
        })
        .replace(MASKED_PROTEIN_RE, (maskedId) => {
            const formatted = formatMaskedProteinId(maskedId, aliasMap, options);
            return formatted === maskedId ? maskedId : escapeMarkdownInline(formatted);
        });
}

export function createCompoundAliasRegistry({
    store = defaultCompoundAliasStore,
    now = () => Date.now(),
} = {}) {
    let current = {
        workspaceFingerprint: null,
        workspaceAliasScope: null,
        aliases: {},
        proteinAliases: {},
        updatedAt: null,
        version: STORE_VERSION,
    };
    let currentSignature = aliasSignature(current.aliases);
    let lastPersistedKey = null;
    const listeners = new Set();

    function notify() {
        listeners.forEach((listener) => {
            try {
                listener();
            } catch (_) {
                // Listener errors should not affect campaign execution.
            }
        });
    }

    function getSnapshot() {
        return current;
    }

    function subscribe(listener) {
        if (typeof listener !== 'function') return () => {};
        listeners.add(listener);
        return () => listeners.delete(listener);
    }

    function refreshFromWorkspace(workspace, maskedSnapshot = {}) {
        try {
            const fingerprint = workspaceFingerprint(workspace, maskedSnapshot);
            const scope = workspaceAliasScope(maskedSnapshot);
            const activeAliases = buildAliasMap(workspace, maskedSnapshot);
            const proteinAliases = buildProteinAliasMap(workspace, maskedSnapshot);
            const canAdoptHydratedScope = scope
                && current.workspaceAliasScope === null
                && current.workspaceFingerprint !== null;
            const aliases = scope
                && (scope === current.workspaceAliasScope || canAdoptHydratedScope)
                ? { ...current.aliases, ...activeAliases }
                : activeAliases;
            const signature = aliasSignature(aliases);
            const changed = fingerprint !== current.workspaceFingerprint
                || signature !== currentSignature
                // A structure rename or a target/off-target switch changes only
                // this map, and the panel reads it to label the docking site.
                || JSON.stringify(proteinAliases) !== JSON.stringify(current.proteinAliases);
            if (!changed) return current;

            current = {
                workspaceFingerprint: fingerprint,
                workspaceAliasScope: scope,
                aliases,
                proteinAliases,
                updatedAt: now(),
                version: STORE_VERSION,
            };
            currentSignature = signature;
            notify();

            const persistKey = `${fingerprint}:${signature}`;
            if (persistKey !== lastPersistedKey && store?.putAliasMap) {
                lastPersistedKey = persistKey;
                // Structure aliases stay out of the stored record on purpose:
                // they are positional and rebuilt on the next refresh, so
                // persisting them would only create a staler second copy.
                const { proteinAliases: _live, ...persisted } = current;
                Promise.resolve(store.putAliasMap(persisted)).catch(() => {});
            }
        } catch (_) {
            // Alias refresh is best-effort; never break masked snapshot reads.
        }
        return current;
    }

    async function hydrate(workspaceFingerprintValue) {
        if (!workspaceFingerprintValue || !store?.getAliasMap) return current;
        try {
            const record = await store.getAliasMap(workspaceFingerprintValue);
            if (!record || record.workspaceFingerprint !== workspaceFingerprintValue) {
                return current;
            }
            current = {
                workspaceFingerprint: record.workspaceFingerprint,
                workspaceAliasScope: record.workspaceAliasScope || null,
                aliases: record.aliases && typeof record.aliases === 'object' ? record.aliases : {},
                // Structure aliases are positional and recomputed on the next
                // workspace refresh, so a hydrated record carries none.
                proteinAliases: {},
                updatedAt: record.updatedAt || null,
                version: record.version || STORE_VERSION,
            };
            currentSignature = aliasSignature(current.aliases);
            notify();
        } catch (_) {
            // Ignore IndexedDB read failures.
        }
        return current;
    }

    function formatCompoundId(maskedId, options = {}) {
        return formatMaskedCompoundId(maskedId, current, options);
    }

    function formatText(text, options = {}) {
        return formatMaskedText(text, current, options);
    }

    function formatMarkdown(text, options = {}) {
        return formatMaskedMarkdown(text, current, options);
    }

    function resetForTests() {
        current = {
            workspaceFingerprint: null,
            workspaceAliasScope: null,
            aliases: {},
            proteinAliases: {},
            updatedAt: null,
            version: STORE_VERSION,
        };
        currentSignature = aliasSignature(current.aliases);
        lastPersistedKey = null;
        notify();
    }

    return {
        getSnapshot,
        subscribe,
        refreshFromWorkspace,
        hydrate,
        formatCompoundId,
        formatText,
        formatMarkdown,
        resetForTests,
    };
}

export const defaultCompoundAliasRegistry = createCompoundAliasRegistry();
